import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, User as UserIcon, Gift, Copy, Wallet, MapPin, Bookmark, Trash2, Plus } from "lucide-react";
import AppShell from "@/components/AppShell";
import VerifiedBadge from "@/components/VerifiedBadge";
import GTFLogo from "@/components/GTFLogo";
import DateOfBirthPicker from "@/components/DateOfBirthPicker";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const [status, setStatus] = useState({ is_verified: !!user?.is_verified, kyc: null });
  const [ref, setRef] = useState({ code: "", invited: 0, wallet_balance: 0 });
  const [places, setPlaces] = useState([]);
  const [placeName, setPlaceName] = useState("");
  const [form, setForm] = useState({ id_type: "aadhaar", id_number: "", full_name_on_id: user?.full_name || "", id_photo: "", selfie_photo: "" });
  const [busy, setBusy] = useState(false);
  const [savingPlace, setSavingPlace] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [], min_payout: 50 });
  const [showPayout, setShowPayout] = useState(false);
  const [payout, setPayout] = useState({ amount: "", upi_id: "" });
  const [payingOut, setPayingOut] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({ full_name: user?.full_name || "", email: user?.email || "", dob: user?.dob || "", village: user?.village || "", district: user?.district || "", state: user?.state || "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/profile", profile);
      setUser({ ...user, ...profile });
      toast.success("Profile updated");
      setEditing(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSavingProfile(false); }
  };

  useEffect(() => {
    api.get("/kyc/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/referral/me").then((r) => setRef(r.data)).catch(() => {});
    api.get("/places").then((r) => setPlaces(r.data)).catch(() => {});
    api.get("/wallet/me").then((r) => setWallet(r.data)).catch(() => {});
  }, []);

  const requestPayout = async () => {
    setPayingOut(true);
    try {
      await api.post("/wallet/payout", { amount: parseFloat(payout.amount), upi_id: payout.upi_id.trim() });
      toast.success("Payout requested — you'll receive it within 24h");
      setShowPayout(false); setPayout({ amount: "", upi_id: "" });
      const r = await api.get("/wallet/me"); setWallet(r.data);
      const rr = await api.get("/referral/me"); setRef(rr.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPayingOut(false); }
  };

  const savePlaceHere = () => {
    if (!placeName.trim()) return toast.error("Give this place a name");
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    setSavingPlace(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post("/places", {
            name: placeName.trim(),
            lat: pos.coords.latitude, lng: pos.coords.longitude,
            state: user?.state || "", city: user?.district || "", village: user?.village || "",
          });
          setPlaces((p) => [data, ...p]);
          setPlaceName("");
          toast.success(`Saved: ${data.name}`);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSavingPlace(false); }
      },
      () => { setSavingPlace(false); toast.error("Enable location to save this place"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const deletePlace = async (id) => {
    try { await api.delete(`/places/${id}`); setPlaces((p) => p.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/kyc/submit", form);
      toast.success("KYC verified successfully");
      setStatus({ is_verified: true });
      if (user) setUser({ ...user, is_verified: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-3xl">
        <div className="flex items-center gap-4">
          <GTFLogo size={56} />
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-gray-500">{theme.name}</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              {user?.full_name}
              {status.is_verified && <VerifiedBadge />}
            </h1>
            <div className="mt-2 text-gray-600 text-sm">{user?.mobile}{user?.email ? ` · ${user.email}` : ""}</div>
          </div>
          <button data-testid="edit-profile-btn" onClick={() => setEditing(!editing)}
            className="ml-auto text-sm px-4 py-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50">{editing ? "Cancel" : "Edit profile"}</button>
        </div>

        {editing && (
          <div className="mt-6 rounded-3xl bg-white border border-gray-200 p-6 space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-600">Full name</label>
              <input data-testid="edit-fullname" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Email</label>
              <input data-testid="edit-email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Date of Birth</label>
              <div className="mt-1"><DateOfBirthPicker value={profile.dob} onChange={(v) => setProfile({ ...profile, dob: v })} testid="edit-dob" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600">State</label>
                <input data-testid="edit-state" value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">District</label>
                <input data-testid="edit-district" value={profile.district} onChange={(e) => setProfile({ ...profile, district: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Village</label>
                <input data-testid="edit-village" value={profile.village} onChange={(e) => setProfile({ ...profile, village: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
              </div>
            </div>
            <button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingProfile}
              className={`w-full py-3 rounded-full ${theme.bg} text-white font-semibold disabled:opacity-60`}>
              {savingProfile ? "Saving..." : "Save changes"}
            </button>
          </div>
        )}

        {/* Referral card */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-200 p-8 premium-shadow">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center"><Gift className="h-6 w-6" /></div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-xl font-bold text-gray-900">Refer & Earn ₹50</div>
              <div className="text-sm text-gray-600 mt-1">Share your code. When a friend joins and completes their first order, you both get ₹50 in your GTF wallet.</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-gray-500 flex items-center gap-1 justify-end"><Wallet className="h-3 w-3" />Wallet</div>
              <div className="text-2xl font-bold text-emerald-700">₹{wallet.balance || 0}</div>
              <button data-testid="open-payout" onClick={() => setShowPayout(true)} disabled={(wallet.balance || 0) < wallet.min_payout}
                className={`mt-1 text-[10px] font-semibold uppercase tracking-widest ${(wallet.balance || 0) < wallet.min_payout ? "text-gray-400" : "text-blue-700 hover:underline"}`}>
                Cash out to UPI →
              </button>
            </div>
          </div>
          <div className="mt-5 flex gap-3 items-center flex-wrap">
            <div className="px-5 py-3 rounded-full bg-white border-2 border-dashed border-amber-400 font-mono font-bold tracking-widest text-lg text-amber-700">{ref.code || "—"}</div>
            <button data-testid="copy-referral" onClick={() => { navigator.clipboard.writeText(ref.code); toast.success("Copied"); }}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold">
              <Copy className="h-4 w-4" />Copy
            </button>
            <button onClick={() => {
              const url = `${window.location.origin}/register?ref=${ref.code}`;
              if (navigator.share) navigator.share({ title: "Join GTF", text: `Use my code ${ref.code} to sign up and earn ₹50!`, url });
              else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
            }} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-800 text-sm font-semibold">
              Share invite
            </button>
            <div className="text-xs text-gray-500 ml-auto">Invited: <b className="text-gray-900">{ref.invited || 0}</b></div>
          </div>
        </div>

        {/* Wallet transactions (only if there are any) */}
        {wallet.transactions && wallet.transactions.length > 0 && (
          <div className="mt-4 rounded-2xl bg-white border border-gray-100 p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 flex items-center gap-1"><Wallet className="h-3 w-3" />Wallet Activity</div>
            <div className="mt-3 space-y-2">
              {wallet.transactions.slice(0, 6).map((tx) => (
                <div key={tx.id} className="flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{tx.type === "payout" ? `Payout to ${tx.upi_id}` : (tx.note || tx.type)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-400">{new Date(tx.created_at).toLocaleDateString()} · {tx.status || "credited"}</div>
                  </div>
                  <div className={`font-bold ${tx.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>{tx.amount < 0 ? "-" : "+"}₹{Math.abs(tx.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Saved Places card */}
        <div className="mt-8 rounded-3xl bg-white border border-gray-100 p-8 premium-shadow">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-2xl ${theme.bgSoft} ${theme.text} flex items-center justify-center`}>
              <Bookmark className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="text-xl font-bold text-gray-900">Saved Places</div>
              <div className="text-sm text-gray-600 mt-1">Save your farm, shop, or work-site so you can post from there in one tap.</div>
            </div>
          </div>

          <div className="mt-6 flex gap-2 flex-wrap">
            <input data-testid="place-name" value={placeName} onChange={(e) => setPlaceName(e.target.value)}
              placeholder='Name this place e.g. "My Farm"'
              className="flex-1 min-w-[220px] rounded-full border border-gray-200 px-5 py-3 text-sm" />
            <button data-testid="save-place-here" onClick={savePlaceHere} disabled={savingPlace}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full ${theme.bg} text-white text-sm font-semibold shadow-lg disabled:opacity-60`}>
              <Plus className="h-4 w-4" />{savingPlace ? "Locating..." : "Save my current location"}
            </button>
          </div>

          {places.length > 0 && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {places.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                  <MapPin className={`h-5 w-5 ${theme.text}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{p.name}</div>
                    <div className="text-xs text-gray-500 truncate">{[p.village, p.city, p.state].filter(Boolean).join(", ") || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`}</div>
                  </div>
                  <button data-testid={`delete-place-${p.id}`} onClick={() => deletePlace(p.id)} className="h-8 w-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* KYC card */}
        <div className="mt-8 rounded-3xl bg-white border border-gray-100 p-8 premium-shadow">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-2xl ${status.is_verified ? "bg-blue-100 text-blue-700" : theme.bgSoft + " " + theme.text} flex items-center justify-center`}>
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="text-xl font-bold text-gray-900">{t("kyc.title")}</div>
              <div className="text-sm text-gray-600 mt-1">{t("kyc.subtitle")}</div>
            </div>
            {status.is_verified && <VerifiedBadge />}
          </div>

          {status.is_verified ? (
            <div className="mt-6 p-4 rounded-2xl bg-blue-50 border border-blue-200 text-blue-800 text-sm">
              You are verified. Your listings will show a trust badge.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600">{t("kyc.id_type")}</label>
                <select value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3">
                  <option value="aadhaar">Aadhaar</option>
                  <option value="pan">PAN</option>
                  <option value="voter">Voter ID</option>
                  <option value="passport">Passport</option>
                  <option value="driving_license">Driving License</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">{t("kyc.id_number")}</label>
                <input data-testid="kyc-id-number" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-600">{t("kyc.name_on_id")}</label>
                <input value={form.full_name_on_id} onChange={(e) => setForm({ ...form, full_name_on_id: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
              </div>
              <PhotoUpload label="ID Photo (front)" testid="kyc-id-photo" value={form.id_photo}
                onChange={(v) => setForm({ ...form, id_photo: v })} />
              <PhotoUpload label="Selfie holding ID" testid="kyc-selfie" value={form.selfie_photo}
                onChange={(v) => setForm({ ...form, selfie_photo: v })} />
              <button data-testid="kyc-submit" disabled={busy}
                className={`md:col-span-2 py-3 rounded-full ${theme.bg} text-white font-semibold shadow-lg disabled:opacity-60`}>
                {busy ? "Verifying..." : t("kyc.submit")}
              </button>
            </form>
          )}
        </div>
      </div>

      {showPayout && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl p-8 premium-shadow">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-emerald-600 text-white flex items-center justify-center"><Wallet className="h-6 w-6" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500">Wallet Payout</div>
                <div className="text-xl font-bold text-gray-900">Cash out to UPI</div>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600">Amount (₹)</label>
                <input data-testid="payout-amount" type="number" min={wallet.min_payout} max={wallet.balance} value={payout.amount}
                  onChange={(e) => setPayout({ ...payout, amount: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" placeholder={`Min ₹${wallet.min_payout} · Max ₹${wallet.balance}`} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">UPI ID</label>
                <input data-testid="payout-upi" value={payout.upi_id} onChange={(e) => setPayout({ ...payout, upi_id: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 font-mono" placeholder="yourname@upi (Paytm/PhonePe/GPay)" />
              </div>
              <div className="text-xs text-gray-500">Payouts are processed within 24 hours. Once submitted, the amount is deducted from your GTF wallet.</div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowPayout(false)} className="flex-1 py-3 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="payout-submit" onClick={requestPayout} disabled={payingOut || !payout.amount || !payout.upi_id}
                className="flex-1 py-3 rounded-full bg-emerald-600 text-white font-semibold disabled:opacity-60">
                {payingOut ? "Requesting..." : "Withdraw"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function PhotoUpload({ label, testid, value, onChange }) {
  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(f);
  };
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600">{label}</label>
      {value ? (
        <div className="mt-1 relative rounded-xl overflow-hidden border border-emerald-300">
          <img src={value} alt={label} className="w-full h-32 object-cover" />
          <button type="button" onClick={() => onChange("")}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white text-xs flex items-center justify-center">×</button>
        </div>
      ) : (
        <label data-testid={testid} className="mt-1 flex items-center justify-center h-32 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50/40 cursor-pointer text-xs text-gray-500">
          <span className="text-center">📸<br />Tap to upload</span>
          <input type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
        </label>
      )}
    </div>
  );
}

